import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useCore } from '../hooks/useCore';
import Loader from '../components/Loader';
import { shortenAddress, formatDate, formatUSDTRaw, PACKAGE_LABELS, PACKAGE_NAMES, PACKAGE_COLORS } from '../utils/helpers';
import '../styles/profile.css';

export default function Profile({ onNotify }) {
  const { account, userId, provider } = useWeb3();
  const { getUserInfo, getIncomeBreakdown, getDirectReferralsByRange, getActiveDirectsForPackage, getLevelMatrixLevelCounts, getLevelMatrixLevelUsers } = useCore();

  const [userInfo, setUserInfo] = useState(null);
  const [income, setIncome] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [totalRefs, setTotalRefs] = useState(0);
  const [activeDirects, setActiveDirects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refPage, setRefPage] = useState(0);
  const [refPageData, setRefPageData] = useState([]); // [{id, packages}]
  const [refLoading, setRefLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const REF_PAGE_SIZE = 10;

  // 15 Level team data
  const [levels, setLevels] = useState([]); // [{level, count, userIds}]
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const [totalTeam, setTotalTeam] = useState(0);

  useEffect(() => {
    if (provider && userId) {
      loadProfile();
    }
    // eslint-disable-next-line
  }, [userId, provider]);

  async function loadProfile() {
    setLoading(true);
    try {
      const [info, inc] = await Promise.all([
        getUserInfo(userId),
        getIncomeBreakdown(userId),
      ]);
      setUserInfo(info);
      setIncome(inc);

      // Get total refs count
      const refsData = await getDirectReferralsByRange(userId, 0, 1);
      setTotalRefs(parseInt(refsData.total));

      const directs = [];
      for (let i = 0; i < 7; i++) {
        const count = await getActiveDirectsForPackage(userId, i);
        directs.push(count);
      }
      setActiveDirects(directs);

      // Load first page of referrals with packages
      await loadRefPage(0);

      // Load 15 levels
      loadLevels();
    } catch {}
    setLoading(false);
  }

  async function loadRefPage(pageNum) {
    setRefLoading(true);
    try {
      const offset = pageNum * REF_PAGE_SIZE;
      const refsData = await getDirectReferralsByRange(userId, offset, REF_PAGE_SIZE);
      setTotalRefs(parseInt(refsData.total));

      // Fetch package info for each referral
      const refDetails = await Promise.all(
        refsData.refs.map(async (refId) => {
          const info = await getUserInfo(refId);
          return { id: refId, packages: info?.packageActive || [] };
        })
      );
      setRefPageData(refDetails);
      setRefPage(pageNum);
    } catch {
      setRefPageData([]);
    }
    setRefLoading(false);
  }

  async function loadLevels() {
    setLevelsLoading(true);
    try {
      // Use on-chain 2X level matrix — package 0 (Starter) for overall team view
      const counts = await getLevelMatrixLevelCounts(userId, 0);
      let total = 0;
      const levelData = counts.map((count, i) => {
        total += count;
        return { level: i + 1, count, userIds: [] };
      });
      setLevels(levelData);
      setTotalTeam(total);
    } catch {
      setLevels([]);
    }
    setLevelsLoading(false);
  }

  async function loadLevelUsers(levelIndex) {
    try {
      const level = levelIndex + 1;
      const userIds = await getLevelMatrixLevelUsers(userId, 0, level);
      setLevels(prev => prev.map((lv, i) => i === levelIndex ? { ...lv, userIds } : lv));
    } catch {}
  }

  const refTotalPages = Math.ceil(totalRefs / REF_PAGE_SIZE);

  function copyReferralLink() {
    const link = `${window.location.origin}?ref=${userId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onNotify) onNotify('Referral link copied!', 'success');
    });
  }

  function copyUserId() {
    navigator.clipboard.writeText(userId).then(() => {
      if (onNotify) onNotify('User ID copied!', 'success');
    });
  }

  if (!account) {
    return (
      <div className="page-container">
        <div className="connect-prompt">
          <div className="connect-icon">&#x1F512;</div>
          <h2>Profile</h2>
          <p>Connect your wallet to view profile</p>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="page-container">
        <div className="connect-prompt">
          <h2>Not Registered</h2>
          <p>Go to Dashboard to register first</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="page-container"><Loader text="Loading profile..." /></div>;
  }

  const currentPkgIdx = userInfo?.packageActive ? [...userInfo.packageActive].lastIndexOf(true) : -1;

  return (
    <div className="page-container">
      <h2 className="page-title">My Profile</h2>

      {/* ===== PROFILE CARD ===== */}
      <div className="profile-card animate-in">
        <div className="profile-avatar">
          <span>{userId?.toString().slice(0, 4)}</span>
        </div>
        <div className="profile-details">
          <div className="profile-row">
            <span className="profile-label">User ID</span>
            <span className="profile-value clickable" onClick={copyUserId}>{userId}</span>
          </div>
          <div className="profile-row">
            <span className="profile-label">Wallet</span>
            <span className="profile-value">{shortenAddress(account)}</span>
          </div>
          <div className="profile-row">
            <span className="profile-label">Referrer</span>
            <span className="profile-value">{userInfo?.referrerId || 'N/A'}</span>
          </div>
          <div className="profile-row">
            <span className="profile-label">Joined</span>
            <span className="profile-value">{formatDate(userInfo?.joinTime)}</span>
          </div>
          <div className="profile-row">
            <span className="profile-label">Current Package</span>
            <span className="profile-value" style={{ color: currentPkgIdx >= 0 ? PACKAGE_COLORS[currentPkgIdx] : 'var(--text-muted)' }}>
              {currentPkgIdx >= 0 ? `${PACKAGE_NAMES[currentPkgIdx]} (${PACKAGE_LABELS[currentPkgIdx]})` : 'None'}
            </span>
          </div>
          <div className="profile-row">
            <span className="profile-label">Total Income</span>
            <span className="profile-value" style={{ color: 'var(--success)' }}>
              ${formatUSDTRaw(userInfo?.totalIncomeEarned)}
            </span>
          </div>
        </div>
      </div>

      {/* ===== REFERRAL LINK ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.05s'}}>
        <h3 className="section-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
          Referral Link
        </h3>
        <div className="referral-link-box">
          <span className="referral-link">{window.location.origin}?ref={userId}</span>
          <button className="btn-copy" onClick={copyReferralLink}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* ===== TEAM OVERVIEW ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.1s'}}>
        <h3 className="section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
          Team Overview
        </h3>
        <div className="team-overview-grid">
          <div className="team-ov-card">
            <span className="tov-value">{userInfo?.totalDirectRefs || 0}</span>
            <span className="tov-label">Direct</span>
          </div>
          <div className="team-ov-card">
            <span className="tov-value">{totalTeam}</span>
            <span className="tov-label">Total Team</span>
          </div>
          <div className="team-ov-card">
            <span className="tov-value">{income ? `$${formatUSDTRaw(income.boardIncome)}` : '$0'}</span>
            <span className="tov-label">Board Income</span>
          </div>
          <div className="team-ov-card">
            <span className="tov-value">{income ? `$${formatUSDTRaw(income.directIncome)}` : '$0'}</span>
            <span className="tov-label">Direct Income</span>
          </div>
          <div className="team-ov-card">
            <span className="tov-value">{income ? `$${formatUSDTRaw(income.levelIncome)}` : '$0'}</span>
            <span className="tov-label">Level Income</span>
          </div>
          <div className="team-ov-card">
            <span className="tov-value">${formatUSDTRaw(userInfo?.totalIncomeEarned)}</span>
            <span className="tov-label">Total Income</span>
          </div>
        </div>
      </div>

      {/* ===== ACTIVE DIRECTS PER PACKAGE ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.15s'}}>
        <h3 className="section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
          Active Directs per Package
        </h3>
        <div className="directs-grid">
          {PACKAGE_NAMES.map((name, i) => (
            <div key={i} className="direct-item" style={{ borderColor: parseInt(activeDirects[i]) > 0 ? PACKAGE_COLORS[i] + '44' : 'var(--glass-border)' }}>
              <span className="direct-pkg">{name}</span>
              <span className="direct-count" style={{ color: parseInt(activeDirects[i]) > 0 ? PACKAGE_COLORS[i] : 'var(--text-muted)' }}>
                {activeDirects[i] || 0}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ===== 15 LEVEL TREE ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.2s'}}>
        <h3 className="section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="5" r="3"/><circle cx="5" cy="19" r="3"/><circle cx="19" cy="19" r="3"/><path d="M12 8v3M7.5 17.2L10.5 11M16.5 17.2L13.5 11"/></svg>
          Level Team ({totalTeam} members)
        </h3>

        {levelsLoading ? (
          <Loader text="Loading levels..." />
        ) : (
          <div className="level-tree">
            {levels.map((lv, idx) => {
              const isExpanded = expandedLevel === idx;
              const hasUsers = lv.count > 0;
              const barWidth = levels.length > 0 && totalTeam > 0 ? Math.max((lv.count / Math.max(...levels.map(l => l.count))) * 100, 4) : 0;

              return (
                <div key={idx} className="level-row">
                  <button
                    className={`level-header ${isExpanded ? 'expanded' : ''} ${hasUsers ? 'has-users' : ''}`}
                    onClick={() => {
                      if (!hasUsers) return;
                      if (isExpanded) { setExpandedLevel(null); }
                      else { setExpandedLevel(idx); if (lv.userIds.length === 0) loadLevelUsers(idx); }
                    }}
                    disabled={!hasUsers}
                  >
                    <div className="level-left">
                      <span className="level-badge">L{lv.level}</span>
                      <div className="level-bar-bg">
                        <div className="level-bar-fill" style={{ width: `${barWidth}%` }}></div>
                      </div>
                    </div>
                    <div className="level-right">
                      <span className="level-count">{lv.count}</span>
                      {hasUsers && (
                        <svg className={`level-arrow ${isExpanded ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                      )}
                    </div>
                  </button>

                  {isExpanded && lv.userIds.length > 0 && (
                    <div className="level-users">
                      {lv.userIds.slice(0, 50).map((uid, j) => (
                        <span key={j} className="level-user-chip">#{uid}</span>
                      ))}
                      {lv.userIds.length > 50 && (
                        <span className="level-user-more">+{lv.userIds.length - 50} more</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== DIRECT REFERRALS LIST ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.25s'}}>
        <h3 className="section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>
          Direct Referrals ({totalRefs})
        </h3>
        {refLoading ? (
          <Loader text="Loading referrals..." />
        ) : refPageData.length > 0 ? (
          <div className="referrals-list">
            {refPageData.map((ref, i) => {
              const serial = refPage * REF_PAGE_SIZE + i + 1;
              return (
                <div key={i} className="referral-item-v2">
                  <div className="ref-serial">{serial}</div>
                  <div className="ref-details">
                    <span className="ref-id-v2">#{ref.id}</span>
                    <div className="ref-pkgs">
                      {PACKAGE_NAMES.map((name, pi) => (
                        <span key={pi} className={`ref-pkg-dot ${ref.packages[pi] ? 'active' : ''}`} title={name} style={ref.packages[pi] ? {background: PACKAGE_COLORS[pi]} : {}}>
                          {name.charAt(0)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Pagination */}
            {refTotalPages > 1 && (
              <div className="pagination" style={{marginTop: 12}}>
                <button className="page-btn" onClick={() => loadRefPage(refPage - 1)} disabled={refPage === 0 || refLoading}>Prev</button>
                <span className="page-info">Page {refPage + 1} / {refTotalPages}</span>
                <button className="page-btn" onClick={() => loadRefPage(refPage + 1)} disabled={refPage >= refTotalPages - 1 || refLoading}>Next</button>
              </div>
            )}
          </div>
        ) : (
          <p className="empty-text">No referrals yet. Share your link!</p>
        )}
      </div>
    </div>
  );
}
